import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HiveStackBaseUrls, HivestackAd, HivestackError } from './types';
import { Log } from '../../../AuxillaryService/Log';
import AdQueue from '../AdQueue';
import { ZodError } from 'zod';
import { VastResponseBodyTag, vastResponseBodyDataFormatter } from '../types';

/**
 * A class that contains a few helper
 * functions to execute various http requests
 * to hivestack's API
 */
export class HivestackApiHelper {
    /**
     * fetches ads from the hivestack API
     * & enqueues fetched ads to the hivestack Ad
     * queue.
     * @see {@link https://docs.hivestack.com/reference/getvast-1 Hivstack API - Request an ad for a single UUID}
     */
    static async fetchAds(
        hivestackUuid: string,
        numberOfAds: number,
        adQueue: AdQueue<HivestackAd>,
        hivestackApiBaseUrl: HiveStackBaseUrls
    ): Promise<void> {
        Log.Info(
            `Hivestack: fetching ads ${numberOfAds} for uuid  ${hivestackUuid}`,
            null,
            __fileline
        );
        const options: AxiosRequestConfig = {
            method: 'GET',
            url: `${hivestackApiBaseUrl}/nirvana/api/v1/units/${hivestackUuid}/schedulevast`,
            timeout: 6000,
        };
        for (let i = 1; i <= numberOfAds; i++) {
            const now: Date = new Date();
            /*
            Hivestack documentation states the expiry for ads are 1 hour 
            after the ad was 'leased'.
            */
            const oneHourFromNow: Date = new Date(now.getTime() + 60 * 60_000);

            const unixSecondsTimestamp: number = Math.floor(oneHourFromNow.getTime() / 1000);

            let response: AxiosResponse;
            try {
                response = await axios.request(options);
            } catch (error: unknown) {
                const errorMessage = this.isHivestackError(error)
                    ? error.response?.data.error
                    : String(error);
                Log.Error(
                    `Hivestack: Failed to fetch ads for UUID ${hivestackUuid} : ${errorMessage}`,
                    null,
                    __fileline
                );
                return;
            }

            if (String(response.data).includes('Nothing scheduled')) {
                Log.Info(`Hivestack: no ads scheduled for UUID ${hivestackUuid}`, null, __fileline);
                return;
            }

            const parser = new DOMParser();

            const xmlDoc = parser.parseFromString(response.data, 'text/xml');

            const adTag = xmlDoc.getElementsByTagName('Ad')[0];
            const adId = adTag.getAttribute('id');
            const mediaFilesTag = adTag.getElementsByTagName('MediaFiles')[0];
            const firstMediaFileTag = mediaFilesTag.getElementsByTagName('MediaFile')[0];
            const creatives = adTag.getElementsByTagName('Creatives')[0];
            const duration = creatives.getElementsByTagName('Duration')[0].textContent;
            const mediaFileTextContent = firstMediaFileTag.textContent;
            const filetype = firstMediaFileTag.getAttribute('type');
            const popUrl = adTag.getElementsByTagName('Impression')[0].textContent;

            const parseResult = vastResponseBodyDataFormatter.safeParse({
                id: adId,
                popUrl: popUrl,
                duration: duration,
                filetype: filetype,
                assetUrl: mediaFileTextContent,
            });

            if (!parseResult.success) {
                const anError: ZodError = parseResult.error;
                Log.Error(
                    `hivestack: Failed to fetch ads for hivestack UUID ${hivestackUuid}; Invalid VAST data recieved: ${anError}`,
                    null,
                    __fileline
                );
                return;
            }
            const adResponseData: VastResponseBodyTag = parseResult.data;

            const anAd: HivestackAd = {
                adId: adResponseData.id,
                assetUrl: adResponseData.assetUrl,
                duration: adResponseData.duration,
                isFallback: false,
                filetype: adResponseData.filetype,
                playcount: 0,
                lastAdInLoop: false,
                popUrl: adResponseData.popUrl,
                expires: unixSecondsTimestamp,
            };

            adQueue.enqueue(anAd);
        }
    }

    /**
     * invokes proof of play for a Hivestack ad
     * via the ad's proof of play url
     */
    static async sendProofOfPlay(
        ad: HivestackAd,
        playerUuid: string | null,
        widgetId: number
    ): Promise<void> {
        const identifier = playerUuid ?? String(widgetId);

        const options: AxiosRequestConfig = {
            method: 'GET',
            url: ad.popUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        };
        try {
            await axios.request(options);
        } catch (error: unknown) {
            const errorMessage = this.isHivestackError(error)
                ? error.response?.data.error
                : String(error);
            Log.Error(
                `hivestack: Failed to send proof of play for ad ${ad.adId} for UUID ${identifier} : ${errorMessage}`,
                null,
                __fileline
            );
            return;
        }
        Log.Info(
            `hivestack: Successfully sent proof of play for ad ${ad.adId} for UUID ${identifier}`,
            null,
            __fileline
        );
    }

    static isHivestackError(err: unknown): err is HivestackError {
        return axios.isAxiosError(err) && typeof err.response?.data.error === 'string';
    }
}
